import { cx } from '@/lib/cx';
import { Button } from '@/components/ui';

/*
 * ── Reordering one project ───────────────────────────────────────────────────
 *
 * Three affordances for two input methods, and the split is not a fallback:
 *
 *  - the GRIP is the drag handle, and drag is a POINTER-ONLY path. ⚠️ Native
 *    HTML5 drag does not work on touch AT ALL — the kanban boards learned this
 *    and had to grow a per-card Move menu for exactly that reason — so the grip
 *    hides itself on a coarse pointer rather than sitting there inert;
 *  - MOVE UP / MOVE DOWN are the touch path AND the keyboard path, which is why
 *    building them once costs nothing extra. They are ordinary buttons: tab to
 *    them, press Enter or Space.
 *
 * ⚠️ **The grip does not become the keyboard path by being a `<button>`.** It
 * is `aria-hidden` decoration with a `title`, because a button whose click does
 * nothing is worse than no button — a reader presses it, nothing happens, and
 * nothing says why. The two arrows carry the real labels, and they name the
 * project so a screen reader meeting twenty "Move up" buttons can tell them
 * apart.
 *
 * A deliberate departure from `KanbanCard`'s Move menu, which is the precedent
 * for the touch half: there the destinations are five named columns and a menu
 * is the only way to list them. Here the destination is a POSITION in a list of
 * up to a hundred rows, which no menu can enumerate — so the reachable
 * destinations are "one up" and "one down", and the long reach is the drag.
 */

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function GripIcon() {
  return (
    <svg {...stroke} className="h-4 w-4 shrink-0">
      <circle cx="9" cy="6" r="1" />
      <circle cx="15" cy="6" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="9" cy="18" r="1" />
      <circle cx="15" cy="18" r="1" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}

/**
 * Marks the one element a drag may start from.
 *
 * The page puts `draggable` on the `<tr>`, because that is what gives the drag
 * a whole-row drag image — but per the HTML drag spec the drag source is the
 * nearest draggable ANCESTOR, so without this every inline editor in the row
 * would start a row drag on a press-and-twitch. The page watches for a
 * pointerdown inside this marker and ARMS the row for that press only; every
 * other press leaves the row undraggable.
 *
 * That is `KanbanBoard`'s `data-kanban-no-drag` mechanism inverted: there one
 * child is taken OUT of a permanently draggable card, here one child is the
 * only thing that puts a row IN.
 */
export const PROJECT_DRAG_HANDLE_SELECTOR = '[data-project-drag-handle]';

export interface ProjectOrderControlsProps {
  /** Names the project in the button labels, which are read out of context. */
  name: string;
  /**
   * Why reordering is unavailable, or null when it is available.
   *
   * ⚠️ Never a bare `disabled`. A control with no reason makes "you may not
   * write projects", "you are looking at a sorted view" and "this row is
   * already at the top" the same dead button.
   */
  disabledReason: string | null;
  /** A move is in flight for THIS project. */
  moving: boolean;
  /** A move is in flight for SOME project — one at a time. */
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /**
   * Whether to draw the drag grip at all.
   *
   * False in the sub-`lg` card list, which is not a drop surface: rows there
   * are cards in a `<ul>` with no row to anchor a drop on, and the widths that
   * view exists for are the touch widths, where drag is dead anyway. A grip
   * that cannot be dragged is worse than none — it advertises a path that does
   * nothing. The arrows are the whole control there, and they are the same
   * control, not a reduced one.
   */
  showHandle?: boolean;
}

export function ProjectOrderControls({
  name,
  disabledReason,
  moving,
  busy,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  showHandle = true,
}: ProjectOrderControlsProps) {
  const blocked = disabledReason !== null;
  const upReason = blocked
    ? disabledReason
    : !canMoveUp
      ? 'Already first in this view.'
      : busy
        ? 'Another project is being moved.'
        : null;
  const downReason = blocked
    ? disabledReason
    : !canMoveDown
      ? 'Already last in this view.'
      : busy
        ? 'Another project is being moved.'
        : null;

  return (
    <div className="flex items-center gap-0.5">
      {/*
       * Decorative, and removed rather than disabled where dragging cannot
       * work: `pointer-coarse:hidden` because native drag is dead on touch, and
       * `showHandle` because the card list is not a drop surface. The variant
       * follows the base `inline-flex` in Tailwind's output, so it wins on the
       * cascade rather than on class order — same mechanism the kanban card's
       * `max-sm:opacity-100` reveal relies on.
       */}
      {showHandle && (
        <span
          data-project-drag-handle=""
          aria-hidden="true"
          title={disabledReason ?? 'Drag to reorder'}
          className={cx(
            'inline-flex shrink-0 text-fg-subtle pointer-coarse:hidden',
            blocked ? 'cursor-not-allowed opacity-40' : 'cursor-grab active:cursor-grabbing',
          )}
        >
          <GripIcon />
        </span>
      )}

      <Button
        size="sm"
        variant="ghost"
        iconOnly
        leadingIcon={<ArrowUpIcon />}
        loading={moving}
        disabled={upReason !== null}
        onClick={onMoveUp}
        aria-label={`Move ${name} up`}
        {...(upReason ? { title: upReason } : { title: `Move ${name} up` })}
      />
      <Button
        size="sm"
        variant="ghost"
        iconOnly
        leadingIcon={<ArrowDownIcon />}
        disabled={downReason !== null}
        onClick={onMoveDown}
        aria-label={`Move ${name} down`}
        {...(downReason ? { title: downReason } : { title: `Move ${name} down` })}
      />
    </div>
  );
}
